import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { ConsentViewT } from "../../../types/database/consents";
import { diaryKeys } from "../../diaries/api/diaryKeys";
import { useConsentsByDiaryId } from "../api/selectConsentById";
import useConsent from "./useConsent";

interface UseConsentDialogProps {
	diaryId?: string;
	isLoading?: boolean;
}

/**
 * Custom hook to handle consent dialog state and data fetching
 * This hook ensures that the consent dialog is always up-to-date with the latest data
 */
const useConsentDialog = ({
	diaryId,
	isLoading = false,
}: UseConsentDialogProps) => {
	const queryClient = useQueryClient();
	const [localIsLoading, setLocalIsLoading] = useState(isLoading);

	// Fetch consents using the diaryId
	const { data: consents = [], isLoading: isConsentsLoading } =
		useConsentsByDiaryId(diaryId || "", { enabled: !!diaryId });

	// Update local state when props change
	useEffect(() => {
		setLocalIsLoading(isLoading || isConsentsLoading);
	}, [isLoading, isConsentsLoading]);

	// Force refetch diary data when dialog is opened
	useEffect(() => {
		if (diaryId) {
			// Invalidate the diary query to force a refetch
			queryClient.invalidateQueries({
				queryKey: diaryKeys.detail(diaryId),
			});
		}
	}, [diaryId, queryClient]);

	const unsignedConsents = useConsent(consents);
	const hasUnsignedConsents = unsignedConsents.length > 0;

	// If still loading or no consents data available yet, return false
	if (localIsLoading || !consents) {
		return { showDialog: false, unsignedConsents: [], consents: [] };
	}

	// If no unsigned consents, return false
	if (!hasUnsignedConsents) {
		return { showDialog: false, unsignedConsents: [], consents };
	}

	return { showDialog: true, unsignedConsents, consents };
};

export default useConsentDialog;
